/**
 * DiffView component.
 * Displays side-by-side diff using Monaco Editor's diff editor.
 * Shows differences between live and draft configurations.
 */

import { useRef, useEffect, useCallback } from "react";

import type { DiffViewProps } from "./types";

// Monaco types - will be available when Monaco is loaded
type MonacoDiffEditor = {
  dispose: () => void;
  getModifiedEditor: () => {
    getValue: () => string;
    onDidChangeModelContent: (callback: () => void) => { dispose: () => void };
  };
  getOriginalEditor: () => {
    getValue: () => string;
  };
  layout: () => void;
};

type Monaco = {
  editor: {
    createDiffEditor: (
      container: HTMLElement,
      options: Record<string, unknown>
    ) => MonacoDiffEditor;
    createModel: (content: string, language: string) => unknown;
  };
};

declare global {
  interface Window {
    monaco: Monaco;
    MonacoEnvironment: {
      getWorkerUrl: (moduleId: string, label: string) => string;
    };
  }
}

/**
 * DiffView component for showing side-by-side JSON/YAML diffs.
 */
export function DiffView({
  original,
  modified,
  originalVersion,
  modifiedVersion,
  language = "json",
  height = "100%",
  readOnly = true,
  onModifiedChange,
}: DiffViewProps): React.ReactElement {
  const containerRef = useRef<HTMLDivElement>(null);
  const editorRef = useRef<MonacoDiffEditor | null>(null);
  const changeListenerRef = useRef<{ dispose: () => void } | null>(null);

  // Initialize Monaco diff editor
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Wait for Monaco to load
    const initMonaco = (): void => {
      if (!window.monaco) {
        // Retry after a short delay if Monaco isn't loaded yet
        setTimeout(initMonaco, 100);
        return;
      }

      // Clean up previous editor if exists
      if (editorRef.current) {
        editorRef.current.dispose();
        editorRef.current = null;
      }

      // Create diff editor
      const editor = window.monaco.editor.createDiffEditor(container, {
        automaticLayout: true,
        renderSideBySide: true,
        readOnly: readOnly,
        originalEditable: false,
        scrollBeyondLastLine: false,
        minimap: { enabled: false },
        lineNumbers: "on",
        renderOverviewRuler: true,
        theme: getPreferredTheme(),
        fontSize: 13,
        wordWrap: "on",
        diffWordWrap: "on",
        ignoreTrimWhitespace: false,
        renderWhitespace: "boundary",
      });

      editorRef.current = editor;
    };

    initMonaco();

    return () => {
      if (changeListenerRef.current) {
        changeListenerRef.current.dispose();
        changeListenerRef.current = null;
      }
      if (editorRef.current) {
        editorRef.current.dispose();
        editorRef.current = null;
      }
    };
  }, [readOnly]);

  // Update models when content changes
  useEffect(() => {
    const editor = editorRef.current;
    if (!editor || !window.monaco) return;

    // Clean up previous change listener
    if (changeListenerRef.current) {
      changeListenerRef.current.dispose();
      changeListenerRef.current = null;
    }

    const originalModel = window.monaco.editor.createModel(original, language);
    const modifiedModel = window.monaco.editor.createModel(modified, language);

    // Set models using type assertion to handle Monaco's setModel interface
    (
      editor as unknown as { setModel: (input: { original: unknown; modified: unknown }) => void }
    ).setModel({
      original: originalModel,
      modified: modifiedModel,
    });

    // Set up change listener for modified content
    if (onModifiedChange && !readOnly) {
      const modifiedEditor = editor.getModifiedEditor();
      changeListenerRef.current = modifiedEditor.onDidChangeModelContent(() => {
        onModifiedChange(modifiedEditor.getValue());
      });
    }

    return () => {
      if (changeListenerRef.current) {
        changeListenerRef.current.dispose();
        changeListenerRef.current = null;
      }
    };
  }, [original, modified, language, readOnly, onModifiedChange]);

  // Handle resize
  const handleResize = useCallback((): void => {
    if (editorRef.current) {
      editorRef.current.layout();
    }
  }, []);

  useEffect(() => {
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [handleResize]);

  return (
    <div className="diff-view flex flex-col h-full" data-testid="diff-view">
      {/* Header showing version comparison */}
      <div className="diff-view__header flex border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
        <div className="flex-1 px-4 py-2 border-r border-gray-200 dark:border-gray-700">
          <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
            Original{" "}
            {originalVersion !== null && originalVersion !== undefined && (
              <span className="text-blue-600 dark:text-blue-400">(v{originalVersion} - Live)</span>
            )}
            {(originalVersion === null || originalVersion === undefined) && (
              <span className="text-gray-400 dark:text-gray-500">(No live version)</span>
            )}
          </span>
        </div>
        <div className="flex-1 px-4 py-2">
          <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
            Modified{" "}
            {modifiedVersion !== undefined && (
              <span className="text-green-600 dark:text-green-400">
                (v{modifiedVersion} - Draft)
              </span>
            )}
          </span>
        </div>
      </div>

      {/* Monaco diff editor container */}
      <div
        ref={containerRef}
        className="flex-1 min-h-0"
        style={{ height: typeof height === "number" ? `${height}px` : height }}
        data-testid="diff-view-editor"
      />
    </div>
  );
}

/**
 * Get the preferred Monaco theme based on system preference.
 */
function getPreferredTheme(): string {
  if (typeof window === "undefined") return "vs";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "vs-dark" : "vs";
}

export default DiffView;
